import { Box } from '@mui/material'

import { InfoIcon } from '@assets/svgs/InfoIcon'
import { Tooltip } from '@components/shared/Tooltip'

import type { MouseEvent } from 'react'

/**
 * Tooltips informativos dos agrupamentos do Resumo Detalhado (e das seções do
 * Detalhamento do Colaborador).
 *
 * Regra de negócio: cada grupo possui um tooltip único, informando fonte do
 * dado, referência de atualização e editabilidade. Ele é exibido apenas no
 * título do agrupamento (ícone de informação ao lado do nome) — os itens não
 * repetem o texto, para não poluir a listagem. Os textos seguem o mapeamento
 * oficial e NÃO devem ser reescritos/interpretados.
 *
 * Nenhum item individual possui tooltip, incluindo "Horas Extras".
 */

/** Grupos alimentados pelo SAP com substituição M-2 → M-1 no dia 10. */
const SAP_M2_M1_TOOLTIP =
  'Fonte: SAP — Referência: M-2 até o dia 10. Após o dia 10, valor substituído por M-1*. ' +
  'Editável durante o mês da prévia. *Atenção à data de substituição para M-1.'

export const APDATA_TOTAL_TOOLTIP =
  'Fonte: Apdata — Referência: M-1 — Total dos colaboradores (Aplicável a todos os itens do grupo).'

export const APDATA_CALC_TOOLTIP =
  'Fonte: Apdata + Cálculos — Referência: M-1 — Total dos colaboradores (Aplicável a todos os itens do grupo).'

export const INDEMNITIES_TOOLTIP = 'Fonte: Apdata — Referência: M-1.'

const PROJECTIONS_TOOLTIP =
  'Resultado de simulações criadas no detalhamento de colaboradores. Editável durante o mês da prévia.'

export const GENERAL_ADJUST_TOOLTIP = 'Valor editável, somado ou subtraído do Total de custos de pessoal.'

/** Texto do tooltip por código de grupo contábil retornado pela API. */
const GROUP_TOOLTIP_BY_CODE: Record<string, string> = {
  SAL: APDATA_TOTAL_TOOLTIP,
  BEN: APDATA_TOTAL_TOOLTIP,
  ENC: APDATA_CALC_TOOLTIP,
  IND: INDEMNITIES_TOOLTIP,
  PLR: SAP_M2_M1_TOOLTIP,
  TEMP: SAP_M2_M1_TOOLTIP,
  EXAM: SAP_M2_M1_TOOLTIP,
  OUT: SAP_M2_M1_TOOLTIP,
  CUSTOS_TRABALHISTAS: SAP_M2_M1_TOOLTIP,
  PROJ_ADM: PROJECTIONS_TOOLTIP,
  PROJ_RES: PROJECTIONS_TOOLTIP
}

/**
 * Fallback por nome do grupo — usado quando o código contábil não está mapeado
 * (ex.: "Provisão de Dissídios Sindicais", que não possui code fixo).
 */
const GROUP_TOOLTIP_BY_NAME: Record<string, string> = {
  salarios: APDATA_TOTAL_TOOLTIP,
  'salarios com adicionais': APDATA_TOTAL_TOOLTIP,
  beneficios: APDATA_TOTAL_TOOLTIP,
  encargos: APDATA_CALC_TOOLTIP,
  indenizacoes: INDEMNITIES_TOOLTIP,
  plr: SAP_M2_M1_TOOLTIP,
  temporarios: SAP_M2_M1_TOOLTIP,
  'exames ocupacionais': SAP_M2_M1_TOOLTIP,
  outros: SAP_M2_M1_TOOLTIP,
  'custos trabalhistas': SAP_M2_M1_TOOLTIP,
  'provisao de dissidios sindicais': SAP_M2_M1_TOOLTIP,
  'provisao de dissidio sindical': SAP_M2_M1_TOOLTIP,
  'provisao dissidio sindical': SAP_M2_M1_TOOLTIP,
  'dissidios sindicais': SAP_M2_M1_TOOLTIP,
  'projecao admissao': PROJECTIONS_TOOLTIP,
  'projecao rescisao': PROJECTIONS_TOOLTIP,
  'projecoes admissao / rescisao': PROJECTIONS_TOOLTIP,
  'projecoes admissao/rescisao': PROJECTIONS_TOOLTIP,
  'ajuste geral de previa': GENERAL_ADJUST_TOOLTIP
}

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()

/** Resolve o tooltip do grupo priorizando o código contábil e caindo no nome. */
export const getGroupTooltip = (code?: string | null, name?: string | null): string | undefined => {
  if (code && GROUP_TOOLTIP_BY_CODE[code]) return GROUP_TOOLTIP_BY_CODE[code]
  if (name) return GROUP_TOOLTIP_BY_NAME[normalize(name)]
  return undefined
}

const infoIconSx = {
  display: 'inline-flex',
  alignItems: 'center',
  color: '#9E9E9E',
  cursor: 'help',
  flexShrink: 0
}

interface GroupInfoTooltipProps {
  /** Quando ausente (ex.: "Horas Extras"), nenhum ícone é renderizado. */
  title?: string
  /** Cor do ícone — permite contraste em linhas de fundo escuro (ex.: linha fixa de totais). */
  color?: string
  /** Impede que o clique no ícone expanda/recolha o agrupamento. */
  stopPropagation?: boolean
}

/** Ícone "i" com o tooltip do grupo — usado no título do agrupamento e nos itens. */
export const GroupInfoTooltip = ({ title, color, stopPropagation = true }: GroupInfoTooltipProps) => {
  if (!title) return null

  return (
    <Tooltip title={title} placement="top" width="fit-content">
      <Box
        component="span"
        sx={{ ...infoIconSx, ...(color ? { color } : {}) }}
        onClick={stopPropagation ? (event: MouseEvent) => event.stopPropagation() : undefined}
      >
        <InfoIcon />
      </Box>
    </Tooltip>
  )
}
